import { ButtonSecondary, Colors, FOCUS_RING, Icon } from '@amelio/ui-library';

import type { AgendaItemView } from '@/components/MeetingAgendaBuilder/agendaViewModel';
import type {
    ThematicMeetingAgenda as ThematicMeetingAgendaData,
    ThematicMeetingAgendaUpdateRequest,
} from '@/types/thematic-meetings';
import type { ContentLanguage } from '@/utils/contentLanguage';
import type { ReactNode } from 'react';

import { AgendaEditor } from '@/components/MeetingAgendaBuilder/AgendaEditor';
import { useAgendaEditor } from '@/components/MeetingAgendaBuilder/useAgendaEditor';
import { PlanSectionHeading } from '@/components/MeetingPlan/PlanSection';
import { useAmelioTranslations } from '@/hooks';

interface ThematicMeetingAgendaEditorProps {
    agenda: ThematicMeetingAgendaData;
    language: ContentLanguage;
    disabled: boolean;
    /** True while the AI writes the agenda that will replace this one: nothing can be edited, so
     * nothing is lost when it does. */
    isLocked: boolean;
    onSave: (request: ThematicMeetingAgendaUpdateRequest) => void;
    banner: ReactNode;
}

/**
 * The editing half of the agenda: the shared editor state and its view, apart from the section
 * around it so the section can mount it afresh. A topic can be deleted here, which the cycle-review
 * flow has no equivalent of.
 */
export const ThematicMeetingAgendaEditor = ({
    agenda,
    language,
    disabled,
    isLocked,
    onSave,
    banner,
}: ThematicMeetingAgendaEditorProps) => {
    const { t } = useAmelioTranslations();

    const editor = useAgendaEditor({
        agenda,
        language,
        disabled: disabled || isLocked,
        onSave,
    });

    // Deleting a topic is specific to this feature; `AgendaItemRow` is shared with the 360 flow and
    // stays untouched, so the control is a sibling of the row rather than a new prop on it.
    const renderDeleteTopic = (item: AgendaItemView) => (
        <button
            type={'button'}
            onClick={() => editor.topics.remove(item.id)}
            aria-label={t('thematicMeetings.plan.deleteTopic', { title: item.title })}
            className={`${FOCUS_RING} hover:bg-primary/10 mt-3 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full`}
            data-testid={`delete-topic-button-${item.id}`}
        >
            <Icon
                name={'essential-trash'}
                size={'sm'}
                color={Colors.BASE_500}
                ariaHidden
            />
        </button>
    );

    return (
        <AgendaEditor
            editor={editor}
            totalMeetingTimeMinutes={agenda.totalMeetingTimeMinutes}
            disabled={disabled}
            heading={
                <PlanSectionHeading
                    title={t('thematicMeetings.plan.agendaTitle')}
                    description={t('thematicMeetings.plan.agendaDescription')}
                />
            }
            banner={banner}
            openingStatementEditing={'inline'}
            readOnlyAsRecord
            generation={{
                isGenerating: isLocked,
                notice: {
                    title: t('thematicMeetings.plan.agendaGeneration.generatingTitle'),
                    message: t('thematicMeetings.plan.agendaGeneration.generatingMessage'),
                },
            }}
            itemListDataTestId={'thematic-meeting-agenda-item-list'}
            renderItemActions={disabled ? undefined : renderDeleteTopic}
            addTopicControl={
                disabled ? null : (
                    <ButtonSecondary
                        onClick={() => editor.topics.setIsAdding(true)}
                        disabled={!editor.topics.canAdd}
                        size={'sm'}
                        // 36px tall with 12px sides, as the design draws it.
                        className={'mt-4 h-9 px-3'}
                        fitContent
                        leftIcon={
                            <Icon
                                name={'essential-add'}
                                size={'sm'}
                                color={Colors.PRIMARY_900}
                                ariaHidden
                            />
                        }
                        dataTestId={'add-topic-button'}
                    >
                        {t('thematicMeetings.plan.addTopic')}
                    </ButtonSecondary>
                )
            }
        />
    );
};
